/**
 * useSyncQueue / useSyncReplayer (Module 2.7 Phase 3 Part 1).
 *
 * Pair of hooks that surface the IndexedDB pending-mutations queue
 * to React:
 *
 *   useSyncQueueCount   live count + entries, subscribes to the
 *                       queue's notification stream so the sidebar
 *                       badge updates without polling.
 *
 *   useSyncReplayer     mount once at the app shell. Drains the
 *                       queue when the browser fires ``online``,
 *                       every 30 s while online, and on first paint.
 *                       Each replay round invalidates the
 *                       ['field-reports'] React Query key so the
 *                       list view picks up newly-synced records.
 */
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import {
  isRetryableSyncError, syncQueue,
  type SyncQueueEntry,
} from '@/lib/syncQueue'


/** Phase 3 Part 2: if the oldest queued entry is older than this many
 * ms, the replayer pauses and the user is prompted to review the
 * queue manually instead of letting auto-replay quietly apply
 * potentially-stale mutations. 24 h per the brief. */
export const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000


/** Session-scoped flag: ``true`` once the user has clicked
 * "Review queue" on the stale prompt this tab session, so the
 * replayer is allowed to run again until the next reload. */
let _staleAcknowledged = false


export function acknowledgeStaleQueue() {
  _staleAcknowledged = true
}


/** Reset the stale-acknowledged flag. Test-only helper. */
export function _resetStaleAck() {
  _staleAcknowledged = false
}


export function useSyncQueueCount() {
  const [count, setCount] = useState(0)
  const [entries, setEntries] = useState<SyncQueueEntry[]>([])

  useEffect(() => {
    let cancelled = false

    const reload = async () => {
      const list = await syncQueue.getAll()
      if (!cancelled) setEntries(list)
    }

    const unsub = syncQueue.subscribe((n) => {
      if (cancelled) return
      setCount(n)
      reload()
    })

    return () => {
      cancelled = true
      unsub()
    }
  }, [])

  const pending = entries.filter((e) => e.status === 'pending')
  const failed = entries.filter((e) => e.status === 'failed')
  return { count, entries, pending, failed }
}


// ---------------------------------------------------------------------------
// Stale-queue helper
// ---------------------------------------------------------------------------

/** Oldest pending entry age in ms (Infinity when queue empty). */
export function oldestPendingAgeMs(entries: SyncQueueEntry[]): number {
  let min = Infinity
  for (const e of entries) {
    if (e.status === 'pending' && e.createdAt < min) min = e.createdAt
  }
  if (!isFinite(min)) return 0
  return Date.now() - min
}


export function hasStaleQueue(entries: SyncQueueEntry[]): boolean {
  return oldestPendingAgeMs(entries) > STALE_THRESHOLD_MS
}


// ---------------------------------------------------------------------------
// Replayer
// ---------------------------------------------------------------------------

const REPLAY_INTERVAL_MS = 30_000


async function replayOne(entry: SyncQueueEntry): Promise<void> {
  if (!syncQueue.beginProcessing(entry.id)) return
  try {
    await api.request({
      url: entry.request.url,
      method: entry.request.method,
      data: entry.request.body,
    })
    await syncQueue.remove(entry.id)
  } catch (err) {
    if (isRetryableSyncError(err)) {
      // Network / 5xx -- bump attempts and leave the entry pending.
      const e = err as { message?: string }
      await syncQueue.recordAttempt(entry.id, {
        error: e?.message || 'replay failed',
      })
    } else {
      // 4xx with a stable rejection -- park as ``failed`` so the
      // user can review on /sync/pending. Per the brief: server
      // wins for non-photo fields; the queued PATCH gets discarded
      // only after the user (or the page UI) explicitly acks.
      const e = err as {
        response?: {
          status?: number;
          data?: { detail?: string };
        };
        message?: string;
      }
      const code = e?.response?.status ?? 0
      const detail =
        e?.response?.data?.detail
        || e?.message
        || 'request failed'
      await syncQueue.markFailed(entry.id, { code, detail })
    }
  } finally {
    syncQueue.endProcessing(entry.id)
  }
}


/**
 * Drain pending entries.
 *
 * ``force`` bypasses the >24h stale guard -- set when the user
 * clicks "Review and replay" on the stale prompt. ``failed`` entries
 * are skipped; the user retries those one-by-one from the pending
 * page.
 */
async function drainOnce(opts: { force?: boolean } = {}): Promise<number> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 0
  const all = await syncQueue.getAll()
  const pending = all.filter((e) => e.status === 'pending')
  if (pending.length === 0) return 0
  if (!opts.force && !_staleAcknowledged && hasStaleQueue(all)) {
    // Pause auto-replay; the stale prompt will surface and the user
    // will either ack (force=true on next tick) or visit /sync/pending.
    return 0
  }
  let processed = 0
  for (const entry of pending) {
    await replayOne(entry)
    processed += 1
  }
  return processed
}


/** Retry one failed entry inline (called from /sync/pending). */
export async function retryEntry(id: number): Promise<void> {
  await syncQueue.markPending(id)
  const all = await syncQueue.getAll()
  const entry = all.find((e) => e.id === id)
  if (entry) await replayOne(entry)
}


/** Discard one entry (called from /sync/pending). */
export async function discardEntry(id: number): Promise<void> {
  await syncQueue.remove(id)
}


type StaleNotifier = () => void
let _stalePromptShown = false


export function useSyncReplayer(opts: { onStale?: StaleNotifier } = {}) {
  const qc = useQueryClient()
  const { onStale } = opts

  useEffect(() => {
    if (!syncQueue.isSupported()) return

    let cancelled = false
    let timer: ReturnType<typeof setInterval> | null = null

    const tick = async () => {
      if (cancelled) return
      try {
        const all = await syncQueue.getAll()
        const stale = hasStaleQueue(all)
        if (stale && !_staleAcknowledged && !_stalePromptShown) {
          _stalePromptShown = true
          onStale?.()
        }
        const processed = await drainOnce()
        if (processed > 0) {
          qc.invalidateQueries({ queryKey: ['field-reports'] })
        }
      } catch {
        // Don't let a stray exception kill the interval.
      }
    }

    // Drain immediately on mount so a reload picks up any queue
    // backlog before the user even notices.
    void tick()

    timer = setInterval(tick, REPLAY_INTERVAL_MS)

    const handleOnline = () => {
      void tick()
    }
    window.addEventListener('online', handleOnline)

    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
      window.removeEventListener('online', handleOnline)
    }
  }, [qc, onStale])
}


/** Test-only helper. */
export function _resetStalePromptShown() {
  _stalePromptShown = false
}


// Exposed for the manual "retry now" button and tests.
export { drainOnce }
